import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatAmount, formatDateTime } from '@/lib/format';
import { ewbSubSupplyLabel, useEwayBills, type EwayBillRow } from '@/lib/gst';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useOrgLocale } from '@/lib/org-locale';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/taxes/gst/eway-bills')({ component: EwayBills });

const CHIPS = ['active', 'expired', 'cancelled', 'all'] as const;
type Chip = (typeof CHIPS)[number];

const state = (r: EwayBillRow): Exclude<Chip, 'all'> =>
  r.status === 'cancelled' ? 'cancelled' : r.expired ? 'expired' : 'active';

/** Screen T11 (document 06): Taxes › GST › E-way bills, India clients. */
function EwayBills() {
  const t = useT();
  const chipLabel = (k: Chip): string =>
    k === 'all' ? t('taxPages.einvoice.filter.all') : t(`taxPages.ewayList.${k}`);
  const orgId = useOrgId();
  const { taxPack, currency } = useOrgLocale();
  const q = useEwayBills();
  const [chip, setChip] = useState<Chip>('active');
  const rows = (q.data ?? []).filter((r) => chip === 'all' || state(r) === chip);
  return (
    <>
      <PageHeader
        title={t('taxPages.gst.ewayLink')}
        description={t('taxPages.ewayList.description')}
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : taxPack !== 'in' ? (
        <EmptyState title={t('taxPages.ewayList.indiaOnly')} />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div
            className="mb-3 flex flex-wrap gap-2"
            role="tablist"
            aria-label={t('taxPages.eInvoicing.statusLabel')}
          >
            {CHIPS.map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={chip === k}
                onClick={() => setChip(k)}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  chip === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {chipLabel(k)}
              </button>
            ))}
          </div>
          {rows.length === 0 ? (
            <EmptyState
              title={t('taxPages.ewayList.empty')}
              hint={t('taxPages.ewayList.emptyHint')}
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border bg-card">
              <Table data-testid="eway-bills">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('taxPages.ewayList.colEwbNo')}</TableHead>
                    <TableHead>{t('taxPages.ewayList.colGenerated')}</TableHead>
                    <TableHead>{t('taxPages.ewayList.colDocument')}</TableHead>
                    <TableHead>{t('taxPages.eInvoicing.col.customer')}</TableHead>
                    <TableHead>{t('taxPages.vatReturn.colType')}</TableHead>
                    <TableHead className="text-end">{t('taxPages.gst.value')}</TableHead>
                    <TableHead>{t('taxPages.ewayList.colValidUntil')}</TableHead>
                    <TableHead>{t('taxPages.ewayList.colVehicle')}</TableHead>
                    <TableHead>{t('taxPages.eInvoicing.statusLabel')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="tnum font-medium">{r.ewbNo}</TableCell>
                      <TableCell className="text-xs">{formatDateTime(r.ewbDate)}</TableCell>
                      <TableCell>
                        <Link
                          className="text-primary hover:underline"
                          to={`${
                            r.documentKind === 'delivery_challan'
                              ? '/sales/delivery-challans'
                              : '/sales/invoices'
                          }/${r.documentId}`}
                        >
                          {r.documentNumber}
                        </Link>
                      </TableCell>
                      <TableCell>{r.contactName}</TableCell>
                      <TableCell className="text-xs">
                        {ewbSubSupplyLabel(r.subSupplyType)}
                      </TableCell>
                      <TableCell className="tnum text-end">
                        {currency} {formatAmount(r.total)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.validUntil
                          ? formatDateTime(r.validUntil)
                          : t('taxPages.ewayList.partAOnly')}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.vehicleNo ?? r.transDocNo ?? r.transporterName ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={state(r) === 'active' ? 'default' : 'secondary'}>
                          {chipLabel(state(r))}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </>
  );
}
